import { alpha, createTheme, Theme } from '@mui/material/styles';
import debounce from 'lodash/debounce';
import { StateCreator } from 'zustand';

import { toCssVariables } from './functions/toCssVariables';
import { ThemeState } from './types/ThemeState';
import { ThemeStoreDependencies } from './types/ThemeStoreDependencies';

export const themeStore =
  ({ useLeadChannelStore }: ThemeStoreDependencies): StateCreator<ThemeState> =>
  (set, get) => ({
    breakpoints: {
      lg1: 1336,
      md1: 872,
      md2: 920,
      sm1: 616,
      sm2: 744,
    },
    colors: {
      alertContrastTextColor: '#ffffff',
      alertErrorBackgroundColor: '#fdeded',
      alertErrorTextColor: '#5f2120',
      alertFilledBackgroundColor: '#323232',
      alertInfoBackgroundColor: '#e5f6fd',
      alertInfoTextColor: '#014361',
      borderColor: '#c0c8d8',
      btnDisabledBorderColor: 'rgba(0, 0, 0, 0.12)',
      btnDisabledColor: '#f5f5f5',
      btnDisabledTextColor: 'rgba(0, 0, 0, 0.26)',
      btnSecondaryColor: '#f5f5f5',
      cardBoxShadowColor: '#11182714',
      checkboxBorderColor: '#cbcbcb',
      checkboxContentColor: '#666666',
      checkboxLabelColor: 'rgba(0, 0, 0, 0.87)',
      drawerBtnColor: '#000000',
      drawerLabelColor: '#999999',
      errorColor: '#d32f2f',
      inputBorderColor: 'rgba(0, 0, 0, 0.23)',
      inputDisabledBorderColor: 'rgba(0, 0, 0, 0.26)',
      inputDisabledColor: '#f5f5f5',
      inputDisabledTextColor: 'rgba(0, 0, 0, 0.38)',
      modalBackgroundColor: '#ffffff',
      modalDividerColor: '#c0c8d8',
      primaryColor: '#0C4650',
      progressEmptyColor: '#f5f5f5',
      radioBorderColor: '#c0c8d8',
      stepperContentColor: '#666666',
      stepperLineColor: '#c0c8d8',
      successColor: '#5d9e51',
    },
    measuredPageHeight:
      document.documentElement.clientHeight || window.innerHeight,
    opacity: {
      selectedTint: 0.16,
    },

    computed: {
      get primaryColor(): string {
        const { leadChannel } = useLeadChannelStore.getState();

        return leadChannel.primaryColor || get().colors.primaryColor;
      },
    },

    createTheme: (): Theme => {
      const {
        computed: { primaryColor },
        colors,
        measuredPageHeight,
        opacity,
      } = get();

      return createTheme({
        components: {
          MuiCard: {
            styleOverrides: {
              root: {
                '&:hover .card-title, &:focus-visible .card-title': {
                  textDecoration: 'underline',
                },
                '&:hover, &:focus-visible': {
                  boxShadow: `0px 12px 28px 0px ${colors.cardBoxShadowColor}`,
                },
                borderRadius: '4px',
                boxShadow: 'none',
                color: 'inherit',
                cursor: 'pointer',
                display: 'block',
                overflow: 'hidden',
                padding: 0,
                textDecoration: 'none',
                transition: 'box-shadow 0.2s ease',
              },
            },
          },
          MuiCardContent: {
            styleOverrides: {
              root: {
                '&:last-child': {
                  paddingBottom: '3rem',
                },
                maxHeight: '9rem',
                padding: '1.5rem 1.5rem 3rem 1.5rem',
              },
            },
          },
          /**
           * use MuiCssBaseline for specific theme assignments to custom classes.
           */
          MuiCssBaseline: {
            styleOverrides: {
              '.navigation-pane': {
                '& .MuiButtonBase-root.Mui-selected': {
                  backgroundColor: alpha(primaryColor, opacity.selectedTint),
                  color: primaryColor,
                },
                '& .MuiButtonBase-root.Mui-selected:hover': {
                  backgroundColor: alpha(primaryColor, opacity.selectedTint),
                },
                '& .MuiListItemIcon-root': {
                  color: 'inherit',
                  minWidth: '2.5rem',
                },
                '& .MuiListItemText-primary': {
                  color: colors.drawerLabelColor,
                  fontWeight: 500,
                  marginBottom: '1rem',
                  padding: '0 1.5rem',
                },
                '& .MuiListItemText-secondary': {
                  color: 'inherit',
                  fontSize: '1rem',
                  fontWeight: 600,
                  lineHeight: '1.5rem',
                },
                a: {
                  borderRadius: '8px',
                  color: colors.drawerLabelColor,
                  height: '4.5rem',
                  marginBottom: '11px',
                  padding: '1.5rem',
                },
              },
            },
          },
          MuiDrawer: {
            defaultProps: {
              PaperProps: {
                style: {
                  padding: '52px 0',
                  width: '17rem',
                },
              },
            },
            styleOverrides: {
              root: {
                '& .navigation-pane a': {
                  borderRadius: '0',
                },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                fontWeight: 500,
              },
            },
          },
          MuiModal: {
            styleOverrides: {
              root: {
                '& .MuiBox-root': {
                  background: colors.modalBackgroundColor,
                  height: '100%',
                  outline: 'none',
                  overflowY: 'auto',
                  padding: '0 1.5rem',
                  width: 'min(100%, var(--modal-width))',
                },
                '& .MuiStepContent-last': {
                  borderLeft: '1px solid transparent',
                },
                '& .MuiStepper-root': {
                  padding: '0',
                },
                '& .MuiStepper-root .MuiCollapse-wrapperInner': {
                  WebkitLineClamp: 'unset',
                },
                '& footer': {
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'right',
                  padding: '1.5rem 0 3rem 0',
                },
                '& header': {
                  background: 'white',
                  borderBottom: `1px solid ${colors.modalDividerColor}`,
                  display: 'flex',
                  h1: {
                    color: 'black',
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    margin: '0',
                  },
                  justifyContent: 'space-between',
                  padding: '3rem 0',
                  position: 'sticky',
                  top: '0',
                },
                '& header button': {
                  alignItems: 'center',
                  color: 'black',
                  cursor: 'pointer',
                  display: 'flex',
                  padding: '0',
                },
                '& main': {
                  paddingTop: '3rem',
                },
                '& main p': {
                  margin: '0',
                  paddingBottom: '1.5rem',
                },
                '@media (min-width: 576px)': {
                  '& .MuiBox-root': {
                    borderRadius: '0.5rem',
                    height: 'unset',
                    minHeight: '435px',
                  },
                  '& footer button': {
                    maxWidth: '248px',
                  },
                  '& main': {
                    maxHeight: '508px',
                    minHeight: '174px',
                    overflowY: 'auto',
                  },
                },
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                '&:hover:not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: primaryColor,
                  },
                // MuiOutlinedInput can be an input or select element - query allows us to target both.
                '*[class*="Mui-disabled"]': {
                  backgroundColor: colors.inputDisabledColor,
                },
                borderRadius: '4px',
                fontSize: '1rem',
                fontWeight: 500,
              },
            },
          },
          MuiPopover: {
            styleOverrides: {
              root: {
                '& .MuiMenuItem-root': {
                  whiteSpace: 'normal',
                },
              },
            },
          },
          MuiSelect: {
            defaultProps: {
              MenuProps: {
                // Ensures the menu opens below the select field
                anchorOrigin: {
                  horizontal: 'left',
                  vertical: 'bottom',
                },
                // Prevent body scroll locking to avoid address bar repositioning on mobile browsers
                disableScrollLock: true,
                slotProps: {
                  paper: {
                    style: {
                      // On certain smaller devices where the screen height is limited, this will prevent
                      // the menu from taking up the entire screen making the first option of the select inaccessible.
                      maxHeight: 'min(max(240px, 30vw), 400px)',
                      minHeight: '120px',
                      // Prevent scroll chaining inside of the menu (hitting the end of menu wont fling the page)
                      overscrollBehavior: 'contain',
                      // Use instant (non-animated) scrolling to the selected option within menu to prevent scroll jumping issues on mobile devices
                      scrollBehavior: 'auto',
                      // Trigger hardware acceleration to improve mobile scrolling performance (especially older devices)
                      transform: 'translateZ(0)',
                    },
                  },
                },
                // Point of which the popover will attach to the anchor's origin
                transformOrigin: {
                  horizontal: 'left',
                  vertical: 'top',
                },
              },
            },
            styleOverrides: {
              icon: {
                color: 'black',
              },
            },
          },
          MuiStepper: {
            styleOverrides: {
              root: {
                '& .Mui-active [class*="MuiStepConnector-line"]': {
                  borderLeft: `1px solid ${primaryColor}`,
                },
                '& .Mui-completed .MuiStepContent-root': {
                  borderLeft: `1px solid ${primaryColor}`,
                },
                '& .Mui-completed [class*="MuiStepConnector-line"]': {
                  borderLeft: `1px solid ${primaryColor}`,
                },
                '& .MuiCollapse-wrapperInner': {
                  display: '-webkit-box',
                  marginBottom: '2rem',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: '3',
                },
                '& .MuiStep-root:not(:first-of-type)': {
                  paddingTop: '0.5rem',
                },
                '& .MuiStepConnector-line': {
                  borderLeft: `1px solid ${colors.stepperLineColor}`,
                },
                // last step being presented in the stepper
                '& .MuiStepContent-last': {
                  paddingBottom: '0.5rem',
                },
                '& .MuiStepContent-root': {
                  borderLeft: `1px solid ${colors.stepperLineColor}`,
                  color: colors.stepperContentColor,
                  fontSize: '1rem',
                  fontWeight: '500',
                  paddingLeft: '2.25rem',
                },
                // last progress step determined via progressStore `isLastStep` method
                '& .MuiStepContent-root.lastProgressStep': {
                  borderLeft: '1px solid transparent',
                },
                '& .MuiStepLabel-iconContainer': {
                  ':not([class*="Mui-active"]):not([class*="Mui-completed"])': {
                    color: colors.stepperLineColor,
                  },
                  color: primaryColor,
                  paddingRight: '0',
                },
                '& .MuiStepLabel-label': {
                  color: colors.stepperContentColor,
                  fontSize: '1rem',
                  fontWeight: '700',
                  paddingLeft: '1.5rem',
                },
                '& .MuiStepLabel-label.Mui-active': {
                  color: 'black',
                  fontSize: '1rem',
                  fontWeight: '700',
                },
                '& .MuiStepLabel-label.Mui-completed': {
                  color: 'black',
                  fontSize: '1rem',
                  fontWeight: '700',
                },
                '& .MuiStepLabel-root': {
                  paddingTop: '0',
                },
                padding: '1.5rem 1.5rem 0 1.5rem',
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: '0.75rem',
                fontWeight: 300,
                lineHeight: '1.2rem',
              },
            },
          },
        },
        pageLayout: {
          display: 'flex',
          flexDirection: 'column',
          height: measuredPageHeight ? `${measuredPageHeight}px` : 'auto',
        },
        palette: {
          primary: {
            main: primaryColor,
          },
        },
        typography: {
          fontFamily: '"Bricolage Grotesque", Verdana, sans-serif',
        },
      });
    },

    publishCssVariables: (): void => {
      const publish = (): void => {
        const {
          colors,
          computed: { primaryColor },
        } = get();

        // defaultPrimaryColor is the brand-neutral default, for surfaces that
        // must not adopt a lead channel's colour. primaryColor last: it is the
        // lead channel override of colors.primaryColor
        Object.entries(
          toCssVariables({
            ...colors,
            defaultPrimaryColor: colors.primaryColor,
            primaryColor,
          }),
        ).forEach(([name, value]): void => {
          document.documentElement.style.setProperty(name, String(value));
        });
      };

      publish();

      // The lead channel resolves after bootstrap, so republish once its
      // branding arrives
      useLeadChannelStore.subscribe(publish);
    },

    initializePageHeightMeasurement: (): void => {
      const { updatePageHeight } = get();

      // Initial page height measurement
      updatePageHeight();

      // Listen on browser resize and orientation change events
      window.addEventListener('resize', updatePageHeight);
      window.addEventListener('orientationchange', updatePageHeight);

      // Observe DOM mutations (ex: inserted/removed nodes that change layout)
      const mutationObserver = new MutationObserver(updatePageHeight);
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Observe element resize (ex: content changes that affect height)
      const resizeObserver = new ResizeObserver(updatePageHeight);
      resizeObserver.observe(document.body);
      resizeObserver.observe(document.documentElement);
    },
    updatePageHeight: debounce((): void => {
      const { measuredPageHeight } = get();

      const LANDSCAPE_RIGHT_DEGREE = 90;
      const LANDSCAPE_LEFT_DEGREE = -90;

      const isLandscape =
        // Safari versions older than 16.4 do not support the orientation property
        // @link https://developer.mozilla.org/en-US/docs/Web/API/Screen/orientation#browser_compatibility
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        screen.orientation?.type.startsWith('landscape') ||
        window.orientation === LANDSCAPE_RIGHT_DEGREE || // support for older browsers
        window.orientation === LANDSCAPE_LEFT_DEGREE;

      const contentOverflows = document.body.scrollHeight > window.innerHeight;

      let rawHeight =
        window.visualViewport?.height || // Prefer visualViewport when available as it accounts for browser zoom scaling
        document.documentElement.clientHeight ||
        window.innerHeight;

      // Capture any zoom styling applied to the document via CSS
      const zoomStyling = parseFloat(
        window
          .getComputedStyle(document.documentElement)
          .getPropertyValue('zoom') || '1',
      );

      // If document zoom styling is applied, compensate for it
      if (zoomStyling !== 1) {
        rawHeight = rawHeight / zoomStyling;
      }

      const newHeight =
        isLandscape && contentOverflows
          ? null // If landscape and content overflows, we don't set a fixed height (defaults to auto)
          : Math.floor(rawHeight); // Round down to avoid potential 1px overflow

      if (measuredPageHeight !== newHeight) {
        set({ measuredPageHeight: newHeight });
      }
    }, 100),
  });
